import type { DataGridColumnVisibilityState } from '@ibsys/kit';
import type { TableViewSnapshot } from '@/entities/settings';
import { isRecord } from '@/shared/lib';
import { attributeColumnId, reportBlockColumnId, reportColumnId } from './constants';

/**
 * Колонка списка, которой выборка называет свою колонку (`ConversationColumnRef`):
 * атрибутная — по пути атрибута, колонка заключения — блока либо, без блока, итога заключения.
 * Колонка не распознана — `null`.
 *
 * Выборка приходит состоянием навигации или из хранилища браузера, поэтому форма колонки
 * проверяется здесь же: нераспознанная колонка пропускается, а не отменяет выборку.
 */
const toColumnId = (column: unknown): string | null => {
  if (!isRecord(column)) return null;
  const { attributePath, reportId, criterionId } = column;
  if (typeof attributePath === 'string') return attributePath ? attributeColumnId(attributePath) : null;
  if (typeof reportId !== 'number') return null;
  return typeof criterionId === 'number'
    ? reportBlockColumnId(reportId, criterionId)
    : reportColumnId(reportId, 'rating');
};

/**
 * Видимость колонок слоя выборки: колонки, по которым посчитан элемент дашборда, показаны в
 * дополнение к колонкам пользователя. Колонок у выборки нет — вид колонок слоя не трогается.
 *
 * Уже показанная колонка второй раз не появляется — видимость у неё одна. Колонка, которой у
 * списка нет, не показывается: видимость к составу колонок списка приводит само состояние колонок,
 * а запись о ней остаётся — блоки заключений приходят позже самого списка, и их колонка встанет,
 * как только станет известна.
 *
 * Пишется видимость в вид слоя, а не в личный вид таблицы: снятие выборки возвращает колонки,
 * отложенные при входе в слой, а пресет, сохранённый из выборки, сохраняет их снимком вида.
 */
export const withSelectionColumns = (
  columnVisibility: DataGridColumnVisibilityState | undefined,
  columns: unknown,
): Pick<TableViewSnapshot, 'columnVisibility'> => {
  const columnIds = Array.isArray(columns) ? columns.map(toColumnId).filter((id): id is string => id !== null) : [];
  if (columnIds.length === 0) return {};
  const shown: DataGridColumnVisibilityState = { ...columnVisibility };
  for (const columnId of columnIds) shown[columnId] = true;
  return { columnVisibility: shown };
};
